"use client"

import { useCampaignSubscribers } from "@/(admin)/modules/campaign-subscribers/hooks/useCampaignSubscriber"
import { CAMPAIGN_SUBSCRIBERS } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { CampaignSubscriberFieldType } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

export const useUpsertCampaignSubscribers = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id
  const [initialValues, setInitialValues] = useState<CampaignSubscriberFieldType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false);
  const { loading, campaignOptions, actions } = useCampaignSubscribers()
  const { fetchCampaignSubscriberByIdAndSetForm, handleUpsertCampaignSubscriber, fetchCampaignsData } = actions

  const getNormalizedSnapshot = useCallback(() => {
    return normalizeFormData(form.getFieldsValue(true));
  }, [form])


  const { isChange, checkChange, modal, message, commitSnapshot, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });

  const loadData = useCallback(async () => {
    if (isEditMode && id) {
      const { subscriber, formData } = await fetchCampaignSubscriberByIdAndSetForm(Number(id), form)
      if (subscriber && formData) {
        setIsFormReady(true);
      }
    }
  }, [id, isEditMode, fetchCampaignSubscriberByIdAndSetForm, form])

  useEffect(() => {
    fetchCampaignsData()
    if (isEditMode) loadData()
    else {
      setIsFormReady(true);
    }
  }, [fetchCampaignsData, loadData, isEditMode])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      const result = await handleUpsertCampaignSubscriber(values, id ? Number(id) : undefined)

      if (result.success) {
        commitSnapshot();
        if (!isEditMode) {

          const targetPath = createPath(CAMPAIGN_SUBSCRIBERS.LIST.PATH, searchParams)
          router.push(targetPath)
        }
      }
    } catch (error) {
      console.error("VALIDATION_FAILED:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    const targetPath = createPath(CAMPAIGN_SUBSCRIBERS.LIST.PATH, searchParams)
    guardCancel(targetPath)
  }

  return {
    form,
    isEditMode,
    loading,
    submitting,
    campaignOptions,
    isButtonDisabled: isEditMode ? !isChange : false,
    handlers: {
      handleSubmit,
      handleBack,
      handleFormChange: checkChange,
    },
  }
}